//@ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material';
import { red } from '@mui/material/colors';
import { XGButton, XGModal } from '@xg-cl/xg-cl';
import { useTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import { formatCurrency } from '../../../../../utils/currencyHelper';

/**
 * Functional React component for rendering a custom delete modal component.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {boolean} props.isOpen - Boolean value to indicate whether modal is open.
 * @param {boolean} props.isSubmitting - Boolean value to indicate whether form is submitting.
 * @param {Array} props.checkedPassengers - Array of checked passengers.
 * @param {Function} [props.onClose] - Callback function to handle form closing.
 * @param {Function} [props.onSubmit] - Callback function to handle form submission.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the delete modal component.
 */
const ConfirmDeleteModal = ({
  isOpen,
  isSubmitting,
  checkedPassengers = [],
  bookingData = {},
  onClose = () => {},
  onCancel = () => {},
  onSubmit = () => {},
  ...rest
}) => {
  const [totalPassangers, setTotalPassangers] = useState(0);
  const { t } = useTranslation(['bookings']);
  const { bookingtotal, bookingplans } = bookingData;
  const getAmountByPassenger = useCallback(
    (passengerId) => {
      for (let bookingPlan of bookingplans) {
        for (let passenger of bookingPlan.passengers ?? []) {
          if (passenger.passengerid === passengerId) {
            return bookingPlan.priceperperson;
          }
        }
      }
      return 0;
    },
    [bookingplans]
  );

  useEffect(() => {
    let deductionTotal = 0;
    checkedPassengers.forEach((passenger) => {
      deductionTotal += getAmountByPassenger(passenger?.passengerid);
    });
    setTotalPassangers(bookingtotal - deductionTotal);
  }, [checkedPassengers, bookingplans, bookingtotal, getAmountByPassenger]);

  return (
    <XGModal
      id={'remove-passenger-modal'}
      allowClosing={true}
      data-testid={'remove-passenger-modal'}
      className='booking-panel__modal'
      actions={
        <Box
          className='booking-panel__modal-actions'
          data-testid='booking-panel__modal-actions'
        >
          <Box sx={{ position: 'relative' }}>
            <XGButton
              style={{
                width: 'auto'
              }}
              className='booking-panel__remove-passenger-button w-auto '
              data-testid='update-action-cancel-button'
              id='update-action-cancel-button'
              text={t('bookings:confirmRemoval')}
              type='error'
              size='large'
              variant='outlined'
              onClick={onSubmit}
              disabled={isSubmitting}
            />
            {isSubmitting && (
              <CircularProgress
                id='update-actions-loader'
                size={24}
                sx={{
                  color: red[500],
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px'
                }}
              />
            )}
          </Box>
          <XGButton
            data-testid='update-action-confirm-button'
            id='update-action-confirm-button'
            text={t('common:cancel')}
            type='secondary'
            size='large'
            variant='outlined'
            onClick={onCancel}
            disabled={isSubmitting}
            style={{ width: 'auto' }}
          />
        </Box>
      }
      dismissable={false}
      open={isOpen}
      title={
        <span className='booking-panel__modal-pricing-plan__title'>
          {t('bookings:confirmPassengerRemoval')}
        </span>
      }
      labelledby={t('bookings:confirmPassengerRemoval')}
      maxWidth='lg'
      scroll='body'
      onClose={onClose}
      {...rest}
    >
      <Box className='booking-panel__modal__content'>
        <Box className='booking-panel__modal__total-row'>
          <span className='booking-panel__modal__total-title'>
            {t('bookings:previousBookingTotal')}
          </span>
          <span className='booking-panel__modal__total-title'>
            {formatCurrency(bookingtotal)}
          </span>
        </Box>

        <span className='booking-panel__modal__divider' />

        <Box className='booking-panel__modal__totals-container w-100'>
          <Box className='w-100'>
            {/* 
          TODO:  keep this for near future implementation
          <Box className='booking-panel__modal__total-row'>
            <span className='booking-panel__modal__item-title'>
              Double Room
            </span>
            <span className='booking-panel__modal__item-title'>
              Total: $0,000.00 USD
            </span>
          </Box> */}

            <Box className='w-100 booking-panel__modal__subitems-container'>
              {checkedPassengers?.map((passenger, index) => {
                const passengerAmount = getAmountByPassenger(
                  passenger?.passengerid
                );
                return (
                  <Box className='booking-panel__modal__total-row' key={index}>
                    <span className='booking-panel__modal__subitem-title'>
                      {passenger?.fullname}
                    </span>
                    <span className='booking-panel__modal__subitem-title booking-panel__modal__subitem-total'>
                      - {formatCurrency(passengerAmount)}
                    </span>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <span className='booking-panel__modal__divider' />

        <Box className='booking-panel__modal__total-row'>
          <span className='booking-panel__modal__total-title'>
            {t('bookings:newBookingTotal')}
          </span>
          <span className='booking-panel__modal__total-title'>
            {formatCurrency(totalPassangers)}
          </span>
        </Box>
      </Box>
    </XGModal>
  );
};

ConfirmDeleteModal.propTypes = {
  isOpen: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  bookingData: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  checkedPassengers: PropTypes.array
};

export default ConfirmDeleteModal;
